import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SECONDS = 120;

const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  for (const t of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"]) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
};

// Tap to talk, tap again to stop. Sends the clip to /api/stt/transcribe (whisper-1) and hands the text back.
export const MicButton = ({ onText, context = "", size = 44, className = "", testId = "mic-btn", rounded = false, disabled = false, maxSeconds = MAX_SECONDS, onStateChange }) => {
  const [state, setStateRaw] = useState("idle"); // idle | recording | transcribing
  const setState = (v) => { setStateRaw(v); onStateChange?.(v); };
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const supported = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && pickMime() !== null;

  useEffect(() => () => { clearInterval(timerRef.current); recRef.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const stop = () => {
    clearInterval(timerRef.current);
    const rec = recRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = rec.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size < 1000) { setState("idle"); toast.message("Didn't catch that — hold the mic a little longer."); return; }
        setState("transcribing");
        try {
          const fd = new FormData();
          const ext = type.includes("mp4") ? "mp4" : type.includes("ogg") ? "ogg" : "webm";
          fd.append("audio", blob, `clip.${ext}`);
          fd.append("language", "en");
          fd.append("context", context);
          const res = await fetch(`${API}/stt/transcribe`, { method: "POST", body: fd, credentials: "include" });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.detail || "Couldn't transcribe that.");
          if (data.text) onText?.(data.text); else toast.message("Heard silence. Try again a touch closer to the mic.");
        } catch (err) {
          toast.error(err.message || "Couldn't transcribe that.");
        } finally {
          setState("idle");
        }
      };
      recRef.current = rec;
      rec.start();
      setSeconds(0);
      setState("recording");
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= maxSeconds) stop();
        return s + 1;
      }), 1000);
    } catch (err) {
      toast.error(err?.name === "NotAllowedError" ? "Microphone blocked — allow it in your browser and try again." : "Microphone not available on this device.");
      setState("idle");
    }
  };

  if (!supported) return null;
  const shape = rounded ? "rounded-full" : "";
  const busy = state === "transcribing";

  return (
    <button
      type="button"
      onClick={state === "recording" ? stop : start}
      disabled={disabled || busy}
      data-testid={testId}
      data-state={state}
      aria-label={state === "recording" ? "Stop recording" : "Speak instead of typing"}
      title={state === "recording" ? `Recording… ${seconds}s — tap to stop` : "Speak instead of typing"}
      style={{ width: size, height: size }}
      className={`relative shrink-0 flex items-center justify-center border transition-colors ${shape} ${
        state === "recording"
          ? "bg-red-500/15 border-red-500 text-red-300"
          : "bg-transparent border-[#E7DFD5] text-[#44403C] hover:border-[#F59E0B] hover:text-[#B45309]"
      } disabled:opacity-50 ${className}`}
    >
      {state === "recording" && <span className="absolute inset-0 animate-ping rounded-[inherit] border border-red-500/50" aria-hidden />}
      {busy ? <Loader2 size={16} className="animate-spin text-[#B45309]" /> : state === "recording" ? <Square size={14} fill="currentColor" /> : <Mic size={16} />}
      {state === "recording" && (
        <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 font-mono text-[9px] tracking-[0.2em] text-red-300 whitespace-nowrap" data-testid={`${testId}-timer`}>
          {seconds}s
        </span>
      )}
    </button>
  );
};
